import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useExperiment, useStatsigClient } from "@statsig/react-bindings";
import isEmpty from "lodash/isEmpty";
import isNull from "lodash/isNull";
import { FormattedMessage } from "react-intl";

import type { IAttachment } from "~/types/api-types";
import { BaseNormalizedListing } from "~/types/local-types";

import { useScrollPercentage } from "./use-scroll-percentage";

type UseShareNudgeAlertParams = {
  attachments: IAttachment[];
  listingId: string;
  listing: BaseNormalizedListing;
  onSaveClick: () => void;
  onShareClick: () => void;
};

export function useShareNudgeAlert({
  attachments,
  listingId,
  listing,
  onSaveClick,
  onShareClick,
}: UseShareNudgeAlertParams) {
  // Fetch experiment without logging exposure — we only want to bucket
  // users who actually scroll 50% down the page.
  const { groupName, value } = useExperiment("listing-share-nudge", {
    disableExposureLog: true,
  });
  const { groupName: saveGroupName, value: saveValue } = useExperiment(
    "listing_save_nudge",
    {
      disableExposureLog: true,
    },
  );

  let showSaveExperiment = false;
  if (!isNull(saveGroupName) && !isEmpty(saveValue)) {
    showSaveExperiment = true;
  }

  const { getExperiment } = useStatsigClient();

  const activeValue = showSaveExperiment ? saveValue : value;
  const activeGroupName = showSaveExperiment ? saveGroupName : groupName;

  const isVariant1 = activeValue?.variant && activeGroupName === "variant-1";
  const isVariant2 = activeValue?.variant && activeGroupName === "variant-2";
  const hasScrolledHalf = useScrollPercentage(50);
  const hasTriggered = useRef(false);
  const [isVisible, setIsVisible] = useState(false);
  const experimentName = showSaveExperiment
    ? "listing_save_nudge"
    : "listing-share-nudge";

  useEffect(() => {
    if (hasTriggered.current) return;

    const hasSavedListing = !!attachments.find(
      (attachment) => attachment.value === listingId,
    );

    if (!hasScrolledHalf || hasSavedListing) return;

    // Log exposure for all groups (control, variant-1, variant-2)
    // once the user has scrolled 50%
    getExperiment(experimentName);
    const variantName = showSaveExperiment ? saveGroupName : groupName;
    window.analyticsIntegrations.experimentLoaded(
      experimentName,
      variantName || "control",
    );

    hasTriggered.current = true;

    const variant = isVariant1 ? "variant-1" : isVariant2 ? "variant-2" : null;
    if (variant) {
      setIsVisible(true);
    }
  }, [
    isVariant1,
    isVariant2,
    hasScrolledHalf,
    attachments,
    listingId,
    getExperiment,
    groupName,
    experimentName,
    saveGroupName,
    showSaveExperiment,
  ]);

  const { alertMessage, ctaBtnText } = useMemo(() => {
    if (showSaveExperiment) {
      return {
        alertMessage: isVariant1 ? (
          <FormattedMessage defaultMessage="Not ready to book yet? Save this space to view again later." />
        ) : (
          <FormattedMessage defaultMessage="Exploring options? Save this space to compare with others." />
        ),
        ctaBtnText: <FormattedMessage defaultMessage="Save listing" />,
      };
    }
    return {
      alertMessage: isVariant1 ? (
        <FormattedMessage defaultMessage="Planning with someone? Share this space with them." />
      ) : (
        <FormattedMessage defaultMessage="Know someone who'd love this space? Share it!" />
      ),
      ctaBtnText: <FormattedMessage defaultMessage="Share listing" />,
    };
  }, [showSaveExperiment, isVariant1]);

  const handleShareClick = useCallback(() => {
    setIsVisible(false);
    if (showSaveExperiment) {
      onSaveClick();
    } else {
      window.analyticsIntegrations.shareModalView(
        listing,
        "share listing details nudge",
        false,
      );
      onShareClick();
    }
  }, [listing, onSaveClick, onShareClick, showSaveExperiment]);

  const handleDismiss = useCallback(() => {
    setIsVisible(false);
  }, []);

  return {
    isVisible,
    alertMessage,
    ctaBtnText,
    onShareClick: handleShareClick,
    onDismiss: handleDismiss,
    experimentName,
  };
}
